// FormLocationUpdate.jsx
import { FiCheck, FiCrosshair, FiLoader, FiPlus, FiMinus, FiInfo } from "react-icons/fi";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, LayersControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { PopService } from "../../../../../../../services/PopService";
import { getLocationDetail } from "../../../../../../../utilities/leftjs/getLocationDetail";
import { FullscreenControl } from "../../../../../../../utilities/leftjs/FullScreen";
import { CentralOdcService } from "../../../../../../../services/CentralOdcService";

const { BaseLayer } = LayersControl;

// ============ ChangeView ============
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom || 16, { duration: 1.2 });
    }
  }, [center, zoom, map]);
  return null;
};

// ============ CtrlScrollZoom ============
const CtrlScrollZoom = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        const steps = Math.abs(e.deltaY) > 150 ? 2 : 1;
        if (e.deltaY < 0) map.zoomIn(steps, { animate: true });
        else if (e.deltaY > 0) map.zoomOut(steps, { animate: true });
      }
    };

    container.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => container.removeEventListener("wheel", onWheel, { capture: true });
  }, [map]);

  return null;
};

// ============ ZoomButtons ============
const ZoomButtons = () => {
  const map = useMap();

  const btnBase = {
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(15, 23, 42, 0.9)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(51, 65, 85, 0.5)",
    color: "#e2e8f0",
    cursor: "pointer",
    transition: "all 0.15s ease",
  };

  return (
    <div style={{
      position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
      display: "flex", flexDirection: "column", gap: 2, zIndex: 1000,
      borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    }}>
      <button
        onClick={() => map.zoomIn(null, { animate: true })}
        style={{ ...btnBase, borderRadius: "9px 9px 0 0" }}
        title="Zoom In"
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.3)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15, 23, 42, 0.9)"; }}
      >
        <FiPlus size={16} strokeWidth={3} />
      </button>
      <div style={{ height: 1, background: "rgba(51, 65, 85, 0.4)" }} />
      <button
        onClick={() => map.zoomOut(null, { animate: true })}
        style={{ ...btnBase, borderRadius: "0 0 9px 9px" }}
        title="Zoom Out"
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.3)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15, 23, 42, 0.9)"; }}
      >
        <FiMinus size={16} strokeWidth={3} />
      </button>
    </div>
  );
};

// ============ ScrollHint ============
const ScrollHint = () => {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), 4000);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return null;

  const kbd = (text) => (
    <kbd style={{
      display: "inline-block", padding: "1px 5px", background: "rgba(51,65,85,0.6)",
      borderRadius: 4, fontSize: 9, fontFamily: "monospace", fontWeight: 700, color: "#e2e8f0",
    }}>{text}</kbd>
  );

  return (
    <div style={{
      position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", zIndex: 1000,
      display: "flex", alignItems: "center", gap: 6, padding: "6px 14px",
      background: "rgba(15,23,42,0.88)", backdropFilter: "blur(10px)", borderRadius: 20,
      border: "1px solid rgba(51,65,85,0.4)", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      fontSize: 10, fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap",
      animation: "fadeHint 0.5s ease-out, fadeHintOut 0.5s ease-in 3.5s forwards",
      pointerEvents: "none",
    }}>
      <FiInfo size={12} style={{ color: "#0ea5e9" }} />
      {kbd("Ctrl")} + {kbd("Scroll")} untuk zoom
    </div>
  );
};

// ============ MapClickHandler ============
const MapClickHandler = ({ onLocationSelect, disabled }) => {
  useMapEvents({
    click(e) {
      if (!disabled) onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// ============ Custom Marker ============
const selectedIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// ============ KOMPONEN UTAMA ============
export const FormLocationUpdateCentralOdc = ({ id, data, setRefresh, setOpenUpdate }) => {
  // Form data — latitude & longitude tetap terpisah di state untuk kirim ke backend
  const [formData, setFormData] = useState({
    latitude: "",
    longitude: "",
    address: "",
  });

  // Satu string gabungan untuk input: "-6.17511, 106.827153"
  const [gpsInput, setGpsInput] = useState("");

  const [mapPosition, setMapPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sinkronisasi data awal
  useEffect(() => {
    const lat = data?.latitude ?? "";
    const lng = data?.longitude ?? "";
    setFormData({
      latitude: lat,
      longitude: lng,
      address: data?.address ?? "",
    });
    if (lat && lng) {
      setGpsInput(`${lat}, ${lng}`);
      setMapPosition([parseFloat(lat), parseFloat(lng)]);
    }
  }, [data]);

  const onChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Parse gpsInput → lat & lng
  const parseGpsInput = (value) => {
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng, valid: true };
      }
    }
    return { valid: false };
  };

  // Handler input GPS gabungan
  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);

    const { lat, lng, valid } = parseGpsInput(value);
    if (valid) {
      onChange("latitude", lat);
      onChange("longitude", lng);
      setMapPosition([lat, lng]);
    }
  };

  // Apply koordinat dari input ke peta + fetch alamat
  const applyGpsInput = () => {
    const { lat, lng, valid } = parseGpsInput(gpsInput);
    if (valid) {
      setMapPosition([lat, lng]);
      onChange("latitude", lat);
      onChange("longitude", lng);
      fetchAddress(lat, lng);
    }
  };

  // Fetch alamat (reverse geocoding)
  const fetchAddress = async (lat, lng) => {
    const latStr = typeof lat === "number" ? lat.toFixed(7) : lat;
    const lngStr = typeof lng === "number" ? lng.toFixed(7) : lng;

    setFetchingAddress(true);
    try {
      const locationData = await getLocationDetail(latStr, lngStr);
      if (locationData?.displayName) {
        onChange("address", locationData.displayName);
      }
    } catch (err) {
      console.error("Gagal mengambil alamat:", err);
    } finally {
      setFetchingAddress(false);
    }
  };

  // Fungsi terpusat: update koordinat + gpsInput + alamat
  const handleLocationSelect = async (lat, lng) => {
    const latStr = typeof lat === "number" ? lat.toFixed(7) : lat;
    const lngStr = typeof lng === "number" ? lng.toFixed(7) : lng;

    onChange("latitude", latStr);
    onChange("longitude", lngStr);
    setGpsInput(`${latStr}, ${lngStr}`);
    setMapPosition([parseFloat(latStr), parseFloat(lngStr)]);

    await fetchAddress(latStr, lngStr);
  };

  // Deteksi GPS
  const handleDetectGPS = () => {
    if (!navigator.geolocation) { alert("Browser tidak mendukung Geolocation"); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationSelect(position.coords.latitude, position.coords.longitude);
        setLoading(false);
      },
      (error) => {
        console.error(error);
        alert("Gagal mendapatkan lokasi. Pastikan GPS aktif.");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Submit simpan
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.latitude || !formData.longitude) {
      alert("Koordinat belum tersedia. Pilih titik di peta atau deteksi GPS.");
      return;
    }
    setIsSubmitting(true);
    try {
      await CentralOdcService.locationUpdate({ id, data: formData });
      setRefresh?.(true);
      setOpenUpdate(false);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Gagal menyimpan lokasi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const INPUT_BASE = {
    background: "rgba(15, 23, 42, 0.7)",
    border: "1px solid rgba(51, 65, 85, 0.4)",
    color: "#e2e8f0",
  };
  const INPUT_FOCUS = {
    borderColor: "rgba(0, 180, 216, 0.5)",
    boxShadow: "0 0 0 3px rgba(0, 180, 216, 0.08)",
  };

  const isBusy = loading || fetchingAddress || isSubmitting;

  return (
    <div className="h-full flex flex-col rounded-xl overflow-hidden">
      {/* ==================== PETA ==================== */}
      <div className="flex-1 min-h-[240px] relative" style={{ zIndex: 0 }}>
        {mapPosition ? (
          <MapContainer
            center={mapPosition}
            zoom={16}
            maxZoom={20}
            minZoom={3}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={false}
            className="relative w-full h-full"
          >
            <ChangeView center={mapPosition} zoom={16} />
            <CtrlScrollZoom />

            <LayersControl position="topright">
              <BaseLayer checked name="Google Maps">
                <TileLayer
                  attribution='&copy; Google Maps'
                  url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                  maxZoom={20} maxNativeZoom={20}
                />
              </BaseLayer>
              <BaseLayer name="Google Satellite">
                <TileLayer
                  attribution='&copy; Google Maps'
                  url="https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                  maxZoom={20} maxNativeZoom={20}
                />
              </BaseLayer>
              <BaseLayer name="OpenStreetMap">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={20} maxNativeZoom={19}
                />
              </BaseLayer>
            </LayersControl>

            <Marker position={mapPosition} icon={selectedIcon}>
              <Popup>
                <div className="text-center text-xs">
                  <p className="font-bold">Lokasi Terpilih</p>
                  <p className="text-gray-500 mt-0.5 font-mono">{gpsInput}</p>
                </div>
              </Popup>
            </Marker>

            <MapClickHandler onLocationSelect={handleLocationSelect} disabled={isBusy} />
            {/* <ZoomButtons /> */}

            <div className="absolute right-[10px] bottom-[10px]" style={{ zIndex: 5000 }}>
              <FullscreenControl />
            </div>

            <ScrollHint />
          </MapContainer>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800 text-slate-500 gap-3">
            <FiCrosshair size={32} className="text-slate-600" />
            <div className="text-center px-4">
              <p className="text-sm font-medium text-slate-400">Peta belum tersedia</p>
              <p className="text-[11px] text-slate-600 mt-1">Deteksi GPS atau masukkan koordinat manual</p>
            </div>
          </div>
        )}

        {/* Overlay fetching address */}
        {fetchingAddress && mapPosition && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 px-4 py-2 bg-slate-900/90 backdrop-blur-md rounded-full shadow-lg border border-slate-700">
            <FiLoader className="animate-spin text-cyan-400" size={14} />
            <span className="text-[11px] font-semibold text-slate-200">Mencari alamat...</span>
          </div>
        )}
      </div>

      {/* ==================== FORM ==================== */}
      <form onSubmit={handleSubmit} className="p-3 space-y-2.5 border-t border-slate-700/50 bg-slate-900/80 backdrop-blur-sm">
        {/* GPS Input Gabungan */}
        <div>
          <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">
            GPS
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              placeholder="-6.17511, 106.827153"
              value={gpsInput}
              onChange={handleGpsInputChange}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyGpsInput(); } }}
              className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-12"
              style={INPUT_BASE}
              onFocus={(e) => Object.assign(e.currentTarget.style, INPUT_FOCUS)}
              onBlur={(e) => Object.assign(e.currentTarget.style, INPUT_BASE)}
            />
            {/* Tombol GPS di dalam input */}
            <button
              type="button"
              onClick={handleDetectGPS}
              disabled={isBusy}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
              title="Deteksi GPS"
            >
              {loading ? <FiLoader className="animate-spin" size={14} /> : <FiCrosshair size={14} />}
            </button>
          </div>
          <p className="text-[9px] text-slate-600 mt-1">Format: latitude, longitude — tekan Enter untuk apply</p>
        </div>

        {/* Alamat */}
        <div>
          <label className="text-[8px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">
            Alamat Lengkap
            {fetchingAddress && <FiLoader className="inline ml-1.5 animate-spin text-cyan-400" size={10} />}
          </label>
          <textarea
            rows={2}
            value={formData.address}
            onChange={(e) => onChange("address", e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-xs text-slate-200 outline-none resize-none transition-all"
            style={INPUT_BASE}
            onFocus={(e) => Object.assign(e.currentTarget.style, INPUT_FOCUS)}
            onBlur={(e) => Object.assign(e.currentTarget.style, INPUT_BASE)}
            placeholder="Klik peta atau deteksi GPS untuk mendapatkan alamat otomatis..."
          />
        </div>

        {/* Aksi */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => setOpenUpdate(false)}
            className="flex-1 py-2.5 rounded-lg font-bold text-[11px] text-slate-400 bg-slate-800/60 border border-slate-700 hover:bg-slate-700/60 transition-all"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={isBusy || !formData.latitude || !formData.longitude}
            className="flex-1 py-2.5 rounded-lg font-bold text-[11px] text-white bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-600/20 flex items-center justify-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <FiLoader className="animate-spin" /> : <FiCheck />}
            {isSubmitting ? "Menyimpan..." : "Simpan Lokasi"}
          </button>
        </div>
      </form>

      <style>{`
        @keyframes fadeHint {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes fadeHintOut {
          from { opacity: 1; }
          to   { opacity: 0; pointer-events: none; }
        }
      `}</style>
    </div>
  );
};